import { GOOGLE_MAP_ID, GOOGLE_PLACES_API_KEY, MAP_LIBRARIES } from "@/config/google";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { LatLng } from "@/lib/types/driver-location";
import { useLocationStore } from "@/stores/use-location-store";
import {
    GoogleMap,
    Polyline,
    useJsApiLoader,
} from "@react-google-maps/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Loader } from "../ui/loader";
import { AdvancedMarker } from "./advanced-marker.web";

interface DistanceTravel {
    duration: number;
    distance: number;
}

interface LiveTrackingMapProps {
    mapRef: any;
    markerRef?: any;
    draggable?: boolean;
    pickupLocation?: LatLng | null;
    dropoffLocation?: LatLng | null;
    showUserLocation?: boolean;
    showTruck?: boolean;
    onRegionChange?: (region: {
        latitude: number;
        longitude: number;
        latitudeDelta: number;
        longitudeDelta: number;
    }) => void;
    onDistanceTravelled?: (Travel: DistanceTravel) => void;
    style?: any;
}

const containerStyle = {
    position: "absolute" as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: "100%",
    height: "100%",
};

/** Matches native follow delta (~0.01) for the driver home camera. */
const DRIVER_FOLLOW_ZOOM = 15;

const DEFAULT_CENTER = {
    lat: 51.5074,
    lng: -0.1278,
};

/** Matches native live-tracking-map edgePadding for address / route preview. */
const ROUTE_EDGE_PADDING = { top: 80, right: 50, bottom: 220, left: 50 };

const towTruckSource = require("@/assets/images/tow-truck.png") as
    | string
    | { uri?: string };

const TOW_TRUCK_URI =
    typeof towTruckSource === "string" ? towTruckSource : towTruckSource?.uri;
    console.log("TOW_TRUCK_URI", TOW_TRUCK_URI);
    

export const LiveTrackingMap: React.FC<LiveTrackingMapProps> = ({
    mapRef,
    markerRef,
    draggable = false,
    pickupLocation,
    dropoffLocation,
    showUserLocation = false,
    showTruck = true,
    style,
    onDistanceTravelled,
}) => {
    const { isLoaded, loadError } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: GOOGLE_PLACES_API_KEY,
        libraries: MAP_LIBRARIES as any,
    });

    const { user } = useAuth();
    const { coordinate, driverCoordinate } = useLocationStore();
    const [fetchedRoute, setFetchedRoute] = useState<{
        key: string;
        path: google.maps.LatLng[];
    } | null>(null);
    const googleMapRef = useRef<google.maps.Map | null>(null);
    const hasSetDriverZoomRef = useRef(false);

    const origin = user?.role === "driver" ? coordinate : driverCoordinate;

    const center = useMemo(() => {
        const lat = Number(pickupLocation?.latitude);
        const lng = Number(pickupLocation?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng };
        }
        return DEFAULT_CENTER;
    }, [pickupLocation?.latitude, pickupLocation?.longitude]);

    // After the first paint, leave camera control to fitBounds / panTo.
    // Center and zoom stay on map options for that first paint only. Passing
    // them as GoogleMap props and then removing them makes the library call
    // setCenter(undefined) and setZoom(undefined), which blanks the map.
    const [cameraLocked, setCameraLocked] = useState(false);

    const distanceCallbackRef = useRef(onDistanceTravelled);
    distanceCallbackRef.current = onDistanceTravelled;

    const routeKey =
        pickupLocation && dropoffLocation
            ? `${pickupLocation.latitude},${pickupLocation.longitude}-${dropoffLocation.latitude},${dropoffLocation.longitude}`
            : null;

    const lastRouteKeyRef = useRef<string | null>(null);
    const routeRequestIdRef = useRef(0);

    if (isLoaded && routeKey && lastRouteKeyRef.current !== routeKey) {
        lastRouteKeyRef.current = routeKey;
        const requestId = ++routeRequestIdRef.current;
        const originLat = Number(pickupLocation?.latitude);
        const originLng = Number(pickupLocation?.longitude);
        const destLat = Number(dropoffLocation?.latitude);
        const destLng = Number(dropoffLocation?.longitude);

        if (!isNaN(originLat) && !isNaN(originLng) && !isNaN(destLat) && !isNaN(destLng)) {
            void (async () => {
                try {
                    const { Route } = await google.maps.importLibrary("routes") as any;

                    const { routes } = await Route.computeRoutes({
                        origin: { lat: originLat, lng: originLng },
                        destination: { lat: destLat, lng: destLng },
                        travelMode: 'DRIVING' as any,
                        routingPreference: 'TRAFFIC_AWARE' as any,
                        extraComputations: ['TRAFFIC_ON_POLYLINE'] as any,
                        fields: ['path', 'distanceMeters', 'durationMillis'],
                    });

                    if (requestId !== routeRequestIdRef.current || !routes || routes.length === 0) {
                        return;
                    }

                    const route = routes[0];
                    if (route.path) {
                        setFetchedRoute({ key: routeKey, path: route.path });
                    }

                    if (distanceCallbackRef.current) {
                        const distanceMeters = route.distanceMeters || 0;
                        const durationMillis = Number(route.durationMillis) || 0;
                        const durationSeconds = durationMillis / 1000;

                        distanceCallbackRef.current({
                            duration: durationSeconds / 60,
                            distance: distanceMeters / 1000,
                        });
                    }
                } catch (error) {
                    console.error("Directions error:", error);
                }
            })();
        }
    }

    const path = fetchedRoute?.key === routeKey ? fetchedRoute.path : null;

    const fitMapToCoordinates = useCallback(
        (
            map: google.maps.Map,
            coordinates: { latitude: number; longitude: number }[],
            edgePadding?: { top?: number; right?: number; bottom?: number; left?: number },
        ) => {
            if (coordinates.length === 0) {
                return;
            }

            const bounds = new google.maps.LatLngBounds();
            coordinates.forEach((c) => {
                bounds.extend({ lat: c.latitude, lng: c.longitude });
            });

            const padding = {
                top: edgePadding?.top ?? ROUTE_EDGE_PADDING.top,
                right: edgePadding?.right ?? ROUTE_EDGE_PADDING.right,
                bottom: edgePadding?.bottom ?? ROUTE_EDGE_PADDING.bottom,
                left: edgePadding?.left ?? ROUTE_EDGE_PADDING.left,
            };

            map.fitBounds(bounds, padding);
        },
        [],
    );

    const fitMapToRoute = useCallback(
        (map: google.maps.Map) => {
            const dropoffLat = Number(dropoffLocation?.latitude);
            const dropoffLng = Number(dropoffLocation?.longitude);
            const hasDropoff =
                dropoffLocation != null &&
                Number.isFinite(dropoffLat) &&
                Number.isFinite(dropoffLng);
                

            if (origin) {
                if (hasDropoff) {
                    return;
                }

                const lat = Number(origin.latitude);
                const lng = Number(origin.longitude);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                    return;
                }

                map.panTo({ lat, lng });
                if (!hasSetDriverZoomRef.current) {
                    map.setZoom(DRIVER_FOLLOW_ZOOM);
                    hasSetDriverZoomRef.current = true;
                }
                return;
            }

            const pickupLat = Number(pickupLocation?.latitude);
            const pickupLng = Number(pickupLocation?.longitude);
            if (!Number.isFinite(pickupLat) || !Number.isFinite(pickupLng)) {
                return;
            }

            if (path && path.length > 0) {
                const bounds = new google.maps.LatLngBounds();
                path.forEach((point) => {
                    bounds.extend(point);
                });
                map.fitBounds(bounds, ROUTE_EDGE_PADDING);
                return;
            }

            if (hasDropoff) {
                fitMapToCoordinates(map, [
                    { latitude: pickupLat, longitude: pickupLng },
                    { latitude: dropoffLat, longitude: dropoffLng },
                ]);
                return;
            }

            map.panTo({ lat: pickupLat, lng: pickupLng });
            map.setZoom(13);
        },
        [
            origin,
            path,
            pickupLocation?.latitude,
            pickupLocation?.longitude,
            dropoffLocation?.latitude,
            dropoffLocation?.longitude,
            fitMapToCoordinates,
        ],
    );

    const onLoad = useCallback(
        (map: google.maps.Map) => {
            googleMapRef.current = map;

            if (mapRef) {
                mapRef.current = {
                    fitToCoordinates: (
                        coordinates: { latitude: number; longitude: number }[],
                        options?: { edgePadding?: typeof ROUTE_EDGE_PADDING },
                    ) => {
                        fitMapToCoordinates(map, coordinates, options?.edgePadding);
                    },
                    animateToRegion: (region: {
                        latitude: number;
                        longitude: number;
                        latitudeDelta?: number;
                        longitudeDelta?: number;
                    }) => {
                        map.panTo({ lat: region.latitude, lng: region.longitude });
                        if (region.latitudeDelta != null) {
                            const zoom = Math.round(
                                Math.log2(360 / Math.max(region.latitudeDelta, 0.001)),
                            );
                            map.setZoom(Math.min(Math.max(zoom, 3), 18));
                        }
                    },
                };
            }

            google.maps.event.trigger(map, "resize");
            fitMapToRoute(map);
            requestAnimationFrame(() => {
                google.maps.event.trigger(map, "resize");
                fitMapToRoute(map);
            });
            setCameraLocked(true);
        },
        [fitMapToCoordinates, fitMapToRoute, mapRef],
    );

    // Re-frame when pickup/dropoff or the fetched route path changes.
    useEffect(() => {
        if (!googleMapRef.current) {
            return;
        }
        fitMapToRoute(googleMapRef.current);
    }, [fitMapToRoute]);

    if (loadError) {
        return (
            <View style={[styles.container, style]}>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </View>
            </View>
        );
    }

    if (!isLoaded) {
        return <View style={[styles.container, style]}><Loader /></View>;
    }

    return (
        <View style={[styles.container, style]}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                onLoad={onLoad}
                options={{
                    mapId: GOOGLE_MAP_ID,
                    disableDefaultUI: true,
                    gestureHandling: "greedy",
                    ...(cameraLocked ? {} : { center, zoom: 13 }),
                }}
            >
                {pickupLocation && (
                    <AdvancedMarker
                        position={
                            origin
                                ? { lat: Number(origin.latitude), lng: Number(origin.longitude) }
                                : { lat: Number(pickupLocation.latitude), lng: Number(pickupLocation.longitude) }
                        }
                        iconUrl={showTruck ? TOW_TRUCK_URI : undefined}
                        iconWidth={showTruck ? 40 : undefined}
                        iconHeight={showTruck ? 40 : undefined}
                        anchor={showTruck ? "center" : "bottom"}
                    />
                )}

                {dropoffLocation && (
                    <AdvancedMarker
                        position={{
                            lat: Number(dropoffLocation.latitude),
                            lng: Number(dropoffLocation.longitude),
                        }}
                    />
                )}

                {path && (
                    <Polyline
                        path={path}
                        options={{
                            strokeColor: BrandColors.primary,
                            strokeWeight: 4,
                        }}
                    />
                )}
            </GoogleMap>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        minHeight: 200,
        position: "relative",
    },
});
